// packages/ui/src/components/lightTooltipPlacement.ts
//
// Where `LightTooltip` draws its layer: what MUI Tooltip's Popper.js computes for its default `placement="bottom"`
// with Popper's default modifiers, so the light tooltip lands on the same pixel.
//   - flip: below the anchor; above it when the layer does not fit below but does fit above; below when it fits
//     neither way (flip's fallback is the first placement);
//   - preventOverflow (main axis, padding 0, tether on): centred on the anchor, shifted to stay inside the visible
//     area, but never beyond the point where its edge passes the anchor's far edge (the tether keeps it attached to
//     an anchor that is itself partly off screen);
//   - computeStyles: the offset from the layer's containing block rounded to the device's pixel grid
//     (`toDevicePixels`).
// The layer is the tooltip with its margins (2px around, the 14px or 24px gap on the anchor's side), as MUI's popper
// box is, so the gap is part of the box that has to fit.

/** A box in viewport coordinates (CSS pixels). */
export interface LightTooltipBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Where the layer goes: its top-left corner in viewport coordinates, and whether it is above the anchor. */
export interface LightTooltipPlacement {
  x: number;
  y: number;
  above: boolean;
}

export function placeLightTooltip({
  anchor,
  layer,
  view,
}: {
  anchor: LightTooltipBox;
  /** Only the layer's size counts. */
  layer: Pick<LightTooltipBox, 'width' | 'height'>;
  /** The visible area (the visual viewport). */
  view: LightTooltipBox;
}): LightTooltipPlacement {
  const fitsBelow = anchor.top + anchor.height + layer.height <= view.top + view.height;
  const fitsAbove = anchor.top - layer.height >= view.top;
  const above = !fitsBelow && fitsAbove;
  const centred = anchor.left + anchor.width / 2 - layer.width / 2;
  // Popper's `within(min(boundaryMin, tetherMin), offset, max(boundaryMax, tetherMax))`: the lower bound wins.
  const lowest = Math.min(view.left, anchor.left + anchor.width);
  const highest = Math.max(view.left + view.width - layer.width, anchor.left - layer.width);
  return {
    x: Math.max(lowest, Math.min(centred, highest)),
    y: above ? anchor.top - layer.height : anchor.top + anchor.height,
    above,
  };
}

/** Popper's `roundOffsetsByDPR`: a CSS length on the device's pixel grid. */
export function toDevicePixels(value: number, devicePixelRatio: number): number {
  const dpr = devicePixelRatio || 1;
  return Math.round(value * dpr) / dpr || 0;
}
